/**
 * Item Dialog Utility Functions
 */

// Re-export shared formatting utilities for backward compatibility
export { formatPrice, safeText } from '@core/utils/formatting.js'

/**
 * Get promo badge type based on promotions
 * @param {Array} promotions - Array of promotion objects
 * @returns {'gold'|'blue'|'red'|null}
 */
export function getPromoBadgeType(promotions) {
  if (!promotions || promotions.length === 0) {
    return null
  }

  // Check if any promo has first_order === true
  const hasFirstOrder = promotions.some((p) => p.first_order === true)

  if (hasFirstOrder) {
    return 'gold'
  }

  // Weekly benefit promo
  const hasWeekly = promotions.some((p) => p.weeklyBenefit === true)
  if (hasWeekly) {
    return 'blue'
  }

  return 'red'
}

/**
 * Get promo badge label
 * @param {'gold'|'blue'|'red'|'green'|null} badgeType
 * @returns {string}
 */
export function getPromoBadgeLabel(badgeType) {
  switch (badgeType) {
    case 'gold':
      return 'מבצע הזמנה ראשונה'
    case 'blue':
      return 'הטבה שבועית'
    case 'red':
      return 'מבצע'
    case 'green':
      return 'הנחות כמות'
    default:
      return ''
  }
}

/**
 * Check if item requires box multiples (onlyBox == 0)
 * @param {Object} item
 * @returns {boolean}
 */
export function requiresBoxMultiples(item) {
  // onlyBox: 0, '0', false, null, or undefined means box multiples required
  // onlyBox: 1, '1', or true means any integer >= 1 is allowed
  const val = item?.onlyBox
  return val == null || val === 0 || val === '0' || val === false
}

/**
 * Parse box size from item
 * @param {Object} item
 * @returns {number}
 */
export function parseBoxSize(item) {
  if (!item?.boxSize) return 0
  const parsed = parseInt(item.boxSize, 10)
  return isNaN(parsed) ? 0 : parsed
}

/**
 * Format box size display text
 * @param {Object} item
 * @returns {string|null}
 */
export function formatBoxSizeText(item) {
  const boxSize = parseBoxSize(item)
  if (boxSize <= 0) return null

  if (requiresBoxMultiples(item)) {
    return `${boxSize} יח' (מארז בלבד)`
  }
  return `${boxSize} יח' במארז`
}

/**
 * Resolve the item's display expiry date.
 * Returns the raw dd.mm.yyyy string only when it is a valid date that is at
 * least 30 days away (near-expiry / past dates are hidden); otherwise null.
 * Shared by the item dialog and the immersive catalog so the rule stays in sync.
 * @param {Object} item
 * @returns {string|null}
 */
export function getDisplayExpDate(item) {
  const raw = item?.exp_date
  if (!raw || typeof raw !== 'string') return null
  const parts = raw.split('.')
  if (parts.length !== 3) return null
  const [dd, mm, yyyy] = parts.map(Number)
  if (!dd || !mm || !yyyy) return null
  const expDate = new Date(yyyy, mm - 1, dd)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const daysRemaining = (expDate - today) / 86400000
  return daysRemaining >= 30 ? raw : null
}
